import { Link } from "react-router-dom";
import { Instagram, Linkedin, Cookie } from "lucide-react";
import { LegalDisclaimer } from "@/components/LegalDisclaimer";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";
import { resetCookieConsent } from "@/components/CookieConsentBanner";

interface FooterLink {
  label: string;
  to: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface FooterProps {
  /** Variant style for the footer */
  variant?: "default" | "minimal" | "compact" | "full";
  /** Show Instagram social link */
  showInstagram?: boolean;
  /** Show LinkedIn social link */
  showLinkedin?: boolean;
  /** Show the LegalDisclaimer component */
  showDisclaimer?: boolean;
  /** Show the website URL link */
  showWebsiteLink?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Custom links to display (if not provided, defaults are used) */
  customLinks?: FooterLink[];
}

// Default footer links
const defaultLinks: FooterLink[] = [
  { label: "Sobre", to: "/sobre" },
  { label: "Nota de Confiança", to: "/nota-de-confianca" },
  { label: "Livro de Reclamações", to: "/livro-reclamacoes" },
  { label: "Contactos", to: "/contactos" },
  { label: "Política de Privacidade", to: "/politica-privacidade" },
  { label: "Termos", to: "/termos" },
];

// Compact links for simpler footers
const compactLinks: FooterLink[] = [
  { label: "Sobre", to: "/sobre" },
  { label: "Nota de Confiança", to: "/nota-de-confianca" },
  { label: "Livro de Reclamações", to: "/livro-reclamacoes" },
  { label: "Contactos", to: "/contactos" },
];

// Extended links for main pages
const extendedLinks: FooterLink[] = [
  { label: "Sobre", to: "/sobre" },
  { label: "Nota de Confiança", to: "/nota-de-confianca" },
  { label: "Livro de Reclamações", to: "/livro-reclamacoes" },
  { label: "Contactos", to: "/contactos" },
  { label: "Política de Privacidade", to: "/politica-privacidade" },
  { label: "Governança de Dados", to: "/governanca-dados" },
  { label: "Política de Imagens IA", to: "/politica-imagens-ia" },
  { label: "Termos", to: "/termos" },
  { label: "Reembolsos", to: "/return-policy" },
];

// Navigation sections for full footer
// Note: Inspeções removed from public footer - now exclusive to registered members
const navigationSections: FooterSection[] = [
  {
    title: "Serviços",
    links: [
      { label: "Elevadores", to: "/elevadores" },
      { label: "Elevador Residencial", to: "/elevador-residencial" },
      { label: "Guia Elevador Residencial 2026", to: "/guia-elevador-residencial-portugal-2026" },
      { label: "Estudo: Mobilidade Vertical", to: "/estudo-mobilidade-vertical-portugal-2026" },
      { label: "Como Funciona", to: "/como-funciona" },
      { label: "Auditoria de Elevadores", to: "/auditoria-elevadores" },
      { label: "Checklist Técnico", to: "/checklist-elevadores" },
    ],
  },
  {
    title: "Segurança",
    links: [
      { label: "Segurança de Elevadores", to: "/seguranca-elevadores" },
      { label: "Falhas Comuns", to: "/falhas-elevadores" },
      { label: "Acidentes", to: "/acidentes-elevadores" },
      { label: "Risco Operacional", to: "/risco-operacional-elevadores" },
    ],
  },
  {
    title: "Gestão",
    links: [
      { label: "Síndico e Elevadores", to: "/sindico-elevadores" },
      { label: "Gestão Predial", to: "/gestao-predial-elevadores" },
      { label: "Contratos de Manutenção", to: "/contrato-manutencao-elevadores" },
      { label: "Custos", to: "/custo-elevadores" },
    ],
  },
  {
    title: "Plataforma",
    links: [
      { label: "Software", to: "/software-elevadores" },
      { label: "Plataforma de Auditoria", to: "/plataforma-auditoria-elevadores" },
      { label: "Gestão de Elevadores", to: "/gestao-elevadores" },
      { label: "Score de Risco", to: "/score-risco-elevadores" },
      { label: "FAQ Técnico", to: "/faq-elevadores" },
      { label: "FAQ Geral", to: "/faq" },
    ],
  },
];

export function Footer({
  variant = "default",
  showInstagram = true,
  showLinkedin = false,
  showDisclaimer = true,
  showWebsiteLink = false,
  className,
  customLinks,
}: FooterProps) {
  const currentYear = new Date().getFullYear();
  
  // Determine which links to use
  const links = customLinks ?? (variant === "compact" ? compactLinks : variant === "minimal" ? compactLinks : extendedLinks);

  if (variant === "minimal") {
    return (
      <footer className={cn("bg-muted/50 py-8 border-t", className)}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
            <p>© {currentYear} ElevAdvisor. Todos os direitos reservados.</p>
            <div className="flex gap-6 flex-wrap justify-center md:justify-end items-center">
              {links.map((link) => (
                <Link 
                  key={link.to}
                  to={link.to} 
                  className="hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={resetCookieConsent}
                className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
              >
                <Cookie className="w-3 h-3" />
                Cookies
              </button>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  if (variant === "compact") {
    return (
      <footer className={cn("py-8 border-t border-border", className)}>
        <div className="container text-center space-y-3">
          <p className="text-xs text-muted-foreground">© {currentYear} ElevAdvisor</p>
          <div className="flex gap-4 justify-center flex-wrap text-xs text-muted-foreground items-center">
            {links.map((link) => (
              <Link 
                key={link.to}
                to={link.to} 
                className="hover:text-foreground"
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={resetCookieConsent}
              className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
            >
              <Cookie className="w-3 h-3" />
              Cookies
            </button>
          </div>
          {showDisclaimer && <LegalDisclaimer variant="inline" />}
        </div>
      </footer>
    );
  }

  // Full variant with navigation sections
  if (variant === "full") {
    return (
      <footer className={cn("border-t border-border bg-muted/30 py-12", className)}>
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8 mb-8">
              {/* Brand */}
              <div className="text-center md:text-left">
                <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                  <Logo to="/" className="justify-center md:justify-start" lazy />
                </div>
                <p className="text-sm text-muted-foreground">Governação Técnica Independente</p>
                <p className="text-xs text-muted-foreground mt-1">Rigor Técnico. Decisões Inteligentes.</p>
                
                {/* Engenheiro Responsável */}
                <div className="mt-4 pt-3 border-t border-border/50">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                    Engenheiro Responsável
                  </p>
                  <a 
                    href="https://www.linkedin.com/in/araujoandrelp"
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-foreground hover:text-primary transition-colors"
                  >
                    André Araujo
                    <Linkedin className="w-3.5 h-3.5" />
                  </a>
                  <p className="text-xs text-muted-foreground">
                    Engenheiro Eletrotécnico · Fundador
                  </p>
                </div>
                
                <p className="text-xs text-muted-foreground mt-3">
                  Morada fiscal: Rua Eng. Ferreira Dias 889, 4100-247 Porto, Portugal
                </p>
                {showInstagram && (
                  <a 
                    href="https://www.instagram.com/elevadvisor" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Conteúdos técnicos no Instagram ElevAdvisor"
                  >
                    <Instagram className="w-4 h-4" />
                    <span>Conteúdos técnicos</span>
                  </a>
                )}
              </div>
              
              {/* Navigation Sections */}
              {navigationSections.map((section) => (
                <div key={section.title} className="text-center md:text-left">
                  <p className="font-semibold text-foreground mb-3">{section.title}</p>
                  <nav className="flex flex-col gap-2" aria-label={section.title}>
                    {section.links.map((link) => (
                      <Link 
                        key={link.to}
                        to={link.to} 
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </nav>
                </div>
              ))}
            </div>
            
            <div className="pt-6 border-t border-border space-y-4">
              {showDisclaimer && <LegalDisclaimer variant="inline" className="text-center" />}
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <p className="text-xs text-muted-foreground">
                  © {currentYear} ElevAdvisor. Todos os direitos reservados.
                </p>
                <div className="flex items-center gap-4 flex-wrap justify-center md:justify-end">
                  {links.map((link) => (
                    <Link 
                      key={link.to}
                      to={link.to} 
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                  {showWebsiteLink && (
                    <Link 
                      to="/" 
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      elevadvisor.com
                    </Link>
                  )}
                  <button
                    onClick={resetCookieConsent}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Cookie className="w-3 h-3" />
                    Cookies
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  // Default variant
  return (
    <footer className={cn("bg-muted/30 border-t py-12", className)}>
      <div className="container mx-auto px-4">
        <div className="space-y-6">
          {showDisclaimer && (
            <LegalDisclaimer variant="inline" className="text-center" />
          )}
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col md:flex-row items-center gap-3 flex-wrap">
              <p className="text-xs text-muted-foreground">
                © {currentYear} ElevAdvisor. Todos os direitos reservados.
              </p>
              <span className="hidden md:inline text-muted-foreground/50">|</span>
              <a 
                href="https://www.linkedin.com/in/araujoandrelp" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Eng. Responsável: André Araujo
                <Linkedin className="w-3 h-3" />
              </a>
              <span className="hidden md:inline text-muted-foreground/50">|</span>
              <p className="text-xs text-muted-foreground">
                Morada fiscal: Rua Eng. Ferreira Dias 889, 4100-247 Porto, Portugal
              </p>
              
              {/* Social Links */}
              {(showInstagram || showLinkedin) && (
                <div className="flex items-center gap-2">
                  {showInstagram && (
                    <a 
                      href="https://www.instagram.com/elevadvisor" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="Instagram ElevAdvisor"
                    >
                      <Instagram className="w-4 h-4" />
                    </a>
                  )}
                  {showLinkedin && (
                    <a 
                      href="https://www.linkedin.com/company/elevadvisor" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      aria-label="LinkedIn ElevAdvisor"
                    >
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                </div>
              )}
            </div>
            
            {/* Footer Links */}
            <div className="flex gap-4 flex-wrap justify-center md:justify-end items-center">
              {links.map((link) => (
                <Link 
                  key={link.to}
                  to={link.to} 
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              ))}
              {showWebsiteLink && (
                <Link 
                  to="/" 
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  elevadvisor.com
                </Link>
              )}
              <button
                onClick={resetCookieConsent}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <Cookie className="w-3 h-3" />
                Cookies
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
